import { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/app/common/common-components/ui/Dialog";
import { Input } from "@/app/common/common-components/ui/Input";
import { Label } from "@/app/common/common-components/ui/Label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/app/common/common-components/ui/RadioGroup";
import { Button } from "@/app/common/common-components/ui/Button";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/app/common/common-components/ui/Alert";
import { ContactModel } from "@/app/models/ContactModel";
import { Badge } from "@/app/common/common-components/ui/Badge";
import SimpleTextEditor, {
  SimpleTextEditorRef,
} from "@/app/common/SimpleTextEditor";
import CursorTextarea from "@/app/common/common-components/CursorTextarea";
import TemplateSelectionDialog from "./TemplateSelectionDialog";
import { MessageTemplate } from "@/app/models/MessageTemplateModel";
import apiAgent from "@/app/api/apiAgent";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface NewMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: ContactModel;
  initialMessage: {
    type: "Email" | "SMS" | "Whatsapp";
    date: string;
    message: string;
    subject?: string;
  } | null;
  variables: { label: string; value: string; replace: string }[];
  userId: string;
  isTestMode?: boolean;
}

interface FormData {
  messageType: "Email" | "SMS" | "Whatsapp";
  subject: string;
  content: string;
}

const NewMessageDialog: React.FC<NewMessageDialogProps> = ({
  open,
  onOpenChange,
  contact,
  initialMessage,
  variables,
  userId,
  isTestMode,
}) => {
  const editorRef = useRef<SimpleTextEditorRef>(null);
  const textareaInsertRef = useRef<((text: string) => void) | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const { isPending: isSending, mutate: sendMessage } = useMutation({
    mutationFn: (data: FormData) =>
      apiAgent.Contact.sendMessage({
        templateType: data.messageType,
        subject: data.subject,
        content: data.content,
        contactID: isTestMode ? undefined : contact.contactID,
        isTest: isTestMode,
      }),
    onSuccess: (response) => {
      if (response.status === "success") {
        toast.success("Message sent successfully!");
        queryClient.invalidateQueries({
          queryKey: ["contactMessages", contact.contactID],
        });
      } else {
        toast.error(response.message || "Failed to send message");
      }
      onOpenChange(false);
    },
    onError: (error) => {
      console.error("Error sending message:", error);
      // TODO: Temporary fix for sendMessage endpoint, remove once fixed
      // https://3.basecamp.com/5211461/buckets/26813893/card_tables/cards/9075698222
      toast.error(
        //@ts-ignore
        error.response?.data?.status_message ||
          "Failed to send message. Please try again."
      );
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      messageType: initialMessage?.type,
      subject: "",
      content: "",
    },
    mode: "onChange",
  });

  const watchedType = watch("messageType");

  const contactDetails = JSON.parse(contact.contact);

  const hasEmail = contact.emailAddress !== "";
  const hasNumber = contactDetails.phone !== "";

  const canSendMessage = () => {
    if (watchedType === "Email") {
      return hasEmail;
    } else if (watchedType === "SMS" || watchedType === "Whatsapp") {
      return hasNumber;
    }
    return true;
  };

  const getValidationTitle = () => {
    if (watchedType === "Email" && !hasEmail) {
      return "Email Required";
    } else if (
      (watchedType === "SMS" || watchedType === "Whatsapp") &&
      !hasNumber
    ) {
      return "Mobile Number Required";
    }
    return "Contact Information Required";
  };

  const getValidationMessage = () => {
    if (watchedType === "Email" && !hasEmail) {
      return "This contact doesn't have an email address. Please add an email address to send email messages.";
    } else if (
      (watchedType === "SMS" || watchedType === "Whatsapp") &&
      !hasNumber
    ) {
      return "This contact doesn't have a mobile number. Please add a mobile number to send SMS or WhatsApp messages.";
    }
    return "";
  };

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      reset({
        messageType: initialMessage?.type || "Email",
        subject: initialMessage?.subject || "",
        content: "",
      });
    }
  }, [open, reset, initialMessage]);

  const onSubmit = (data: FormData) => {
    sendMessage(data);
    if (data.messageType === "Whatsapp") {
      // open wa.me link with message
      const contactDetails = JSON.parse(contact.contact);
      const contactPhone =
        contactDetails.phone_dialing_code + contactDetails.phone;
      window.open(
        `https://wa.me/${contactPhone}?text=${encodeURIComponent(data.content)}`,
        "_blank"
      );
      return;
    }
  };

  const handleVariableInsert = (variableValue: string) => {
    if (watchedType === "Email") {
      // For email messages, insert at cursor position
      editorRef.current?.insertTextAtCursor(variableValue + " ");
    } else {
      // For SMS/WhatsApp, insert at cursor position in textarea
      textareaInsertRef.current?.(variableValue + " ");
    }
  };

  const handleTemplateSelect = (template: MessageTemplate) => {
    setValue("messageType", template.templateType);

    // Replace variables in subject with actual values
    let populatedSubject = template.subject || "";
    variables.forEach((variable) => {
      const regex = new RegExp(variable.replace, "g");
      populatedSubject = populatedSubject.replace(regex, variable.value);
    });
    setValue("subject", populatedSubject);

    // Replace variables in content with actual values
    let populatedContent = template.content;
    variables.forEach((variable) => {
      const regex = new RegExp(variable.replace, "g");
      populatedContent = populatedContent.replace(regex, variable.value);
    });

    setValue("content", populatedContent);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-sm:tw-max-w-md tw-max-h-[90vh] tw-overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            New Message to {contact.firstName} {contact.lastName}
          </DialogTitle>
        </DialogHeader>
        <form className="tw-flex tw-flex-col tw-gap-4">
          {!canSendMessage() && (
            <Alert variant="default">
              <AlertTitle>{getValidationTitle()}</AlertTitle>
              <AlertDescription>{getValidationMessage()}</AlertDescription>
            </Alert>
          )}
          <div className="tw-space-y-2">
            <div className="tw-flex tw-items-center tw-justify-between">
              <Controller
                name="messageType"
                control={control}
                rules={{ required: "Message type is required" }}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      setValue("content", "");
                      setValue("subject", "");
                    }}
                    disabled={isSending}
                  >
                    <div className="tw-flex tw-gap-4">
                      <div className="tw-flex tw-items-center tw-space-x-2">
                        <RadioGroupItem value="Email" id="email" />
                        <Label htmlFor="email">Email</Label>
                      </div>
                      <div className="tw-flex tw-items-center tw-space-x-2">
                        <RadioGroupItem value="SMS" id="sms" />
                        <Label htmlFor="sms">SMS</Label>
                      </div>
                      <div className="tw-flex tw-items-center tw-space-x-2">
                        <RadioGroupItem value="Whatsapp" id="whatsapp" />
                        <Label htmlFor="whatsapp">WhatsApp</Label>
                      </div>
                    </div>
                  </RadioGroup>
                )}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setTemplateDialogOpen(true)}
                disabled={isSending}
              >
                Select Template
              </Button>
            </div>
            {errors.messageType && (
              <p className="tw-text-xs tw-text-red-500">
                {errors.messageType.message}
              </p>
            )}
          </div>

          {watchedType === "Email" && (
            <div className="tw-space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                {...register("subject", {
                  validate: (value) => {
                    if (
                      watchedType === "Email" &&
                      (!value || value.trim() === "")
                    ) {
                      return "Subject is required for email messages";
                    }
                    return true;
                  },
                })}
                placeholder="Enter message subject"
                disabled={isSending}
              />
              {errors.subject && (
                <p className="tw-text-xs tw-text-red-500">
                  {errors.subject.message}
                </p>
              )}
            </div>
          )}

          <div className="tw-space-y-2">
            <Label htmlFor="content">Message Content</Label>
            <Controller
              name="content"
              control={control}
              rules={{
                required: "Message content is required",
                validate: (value) => {
                  if (watchedType === "SMS" && value && value.length > 160) {
                    return "SMS content cannot exceed 160 characters";
                  }
                  return true;
                },
              }}
              render={({ field }) => (
                <>
                  {watchedType === "Email" ? (
                    <SimpleTextEditor
                      ref={editorRef}
                      value={field.value}
                      onChange={field.onChange}
                      format="html"
                      disabled={isSending}
                      className="tw-max-w-full"
                    />
                  ) : (
                    <CursorTextarea
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter your message"
                      disabled={isSending}
                      maxLength={watchedType === "SMS" ? 160 : undefined}
                      onInsertTextAtCursor={(insertFn) => {
                        textareaInsertRef.current = insertFn;
                      }}
                    />
                  )}
                </>
              )}
            />
            {errors.content && (
              <p className="tw-text-xs tw-text-red-500">
                {errors.content.message}
              </p>
            )}
            {watchedType === "SMS" && (
              <p className="tw-text-xs tw-text-muted-foreground">
                {watch("content")?.length || 0}/160 characters
              </p>
            )}

            <div className="tw-flex tw-flex-col tw-gap-2 tw-text-xs tw-text-muted-foreground">
              <span className="tw-font-bold">Variables:</span>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                {variables.map((variable) => (
                  <Badge
                    key={variable.label}
                    className="tw-cursor-pointer"
                    onClick={() => handleVariableInsert(variable.value)}
                    variant="outline"
                  >
                    {variable.label}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={(e) => {
                e.preventDefault();
                onOpenChange(false);
              }}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSending || !canSendMessage()}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }}
            >
              {isSending ? "Sending..." : "Send Message"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      <TemplateSelectionDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        userId={userId}
        onTemplateSelect={handleTemplateSelect}
        filterType={watchedType}
      />
    </Dialog>
  );
};

export default NewMessageDialog;
